import React, { useState, useEffect, useCallback } from "react";
import { Plus, Edit2, Trash2, Loader, RefreshCw, Save, CheckCircle, AlertCircle, Image as ImageIcon, X } from "lucide-react";
import { uploadAPI } from "../../services/api";
import axios from "axios";
import { getImageUrl } from "../../utils/image";

// Helper for calling our backend certifications REST API
const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const certsAPI = {
  getAll: () => axios.get(`${BASE_URL}/certifications`),
  create: (d) => axios.post(`${BASE_URL}/certifications`, d, {
    headers: { Authorization: `Bearer ${JSON.parse(localStorage.getItem("sava_admin_user"))?.token}` }
  }),
  update: (id, d) => axios.put(`${BASE_URL}/certifications/${id}`, d, {
    headers: { Authorization: `Bearer ${JSON.parse(localStorage.getItem("sava_admin_user"))?.token}` }
  }),
  delete: (id) => axios.delete(`${BASE_URL}/certifications/${id}`, {
    headers: { Authorization: `Bearer ${JSON.parse(localStorage.getItem("sava_admin_user"))?.token}` }
  }),
};

function CertImageUpload({ onUploaded, label = "Upload Image" }) {
  const [uploading, setUploading] = useState(false);
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { data } = await uploadAPI.single(file, "certifications");
      onUploaded(data.url);
    } catch {
      alert("Upload failed");
    } finally {
      setUploading(false);
    }
  };
  return (
    <label className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 text-xs font-semibold cursor-pointer transition-colors ${uploading ? "opacity-50" : "hover:bg-brand-pale hover:border-brand-primary text-gray-600"}`}>
      {uploading ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
      {label}
      <input type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
    </label>
  );
}

const emptyCert = { name: "", logoUrl: "", imageUrl: "", description: "", sortOrder: 0, isActive: true, featured: false };

export default function AdminCertifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyCert);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const fetchCerts = useCallback(async () => {
    setLoading(true);
    try {
      const r = await certsAPI.getAll();
      setItems(r.data.certifications || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCerts();
  }, [fetchCerts]);

  const f = k => v => setForm(prev => ({ ...prev, [k]: v }));
  const setE = k => e => f(k)(e.target.value);
  const setC = k => e => f(k)(e.target.checked);

  const handleSave = async () => {
    if (!form.name.trim()) { alert("Certification name required"); return; }
    setSaving(true);
    try {
      if (editItem) {
        await certsAPI.update(editItem.id, form);
      } else {
        await certsAPI.create(form);
      }
      setSaved(true);
      setShowForm(false);
      fetchCerts();
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      alert("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this certification?")) return;
    try {
      await certsAPI.delete(id);
      fetchCerts();
    } catch {}
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading font-bold text-gray-900 text-xl">Certifications</h2>
          <p className="text-gray-400 text-sm">Manage quality certificates displayed on About and Home pages</p>
        </div>
        <div className="flex gap-2 items-center">
          {saved && <span className="flex items-center gap-1.5 text-green-700 text-sm bg-green-50 px-3 py-1.5 rounded-xl border border-green-200"><CheckCircle className="w-4 h-4" />Saved!</span>}
          <button onClick={fetchCerts} className="w-9 h-9 flex items-center justify-center bg-white border border-gray-200 rounded-xl hover:bg-gray-50 shadow-soft"><RefreshCw className="w-4 h-4 text-gray-500" /></button>
          <button onClick={() => { setEditItem(null); setForm(emptyCert); setShowForm(true); }} className="btn-primary text-xs px-4 py-2.5"><Plus className="w-4 h-4" /> Add Certificate</button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader className="w-6 h-6 text-brand-primary animate-spin mr-2" /><span className="text-gray-400 text-sm">Loading certifications...</span></div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map(cert => (
            <div key={cert.id} className="bg-white rounded-2xl shadow-card hover:shadow-hover transition-all duration-300 overflow-hidden flex flex-col justify-between border border-gray-50">
              <div className="p-5 flex gap-4">
                <div className="w-16 h-16 bg-brand-pale rounded-xl flex-shrink-0 flex items-center justify-center overflow-hidden border border-gray-100">
                  {cert.logoUrl ? (
                    <img src={getImageUrl(cert.logoUrl)} alt={cert.name} className="w-full h-full object-contain p-1" />
                  ) : (
                    <span className="text-2xl">🏆</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading font-bold text-gray-900 text-sm truncate">{cert.name}</h3>
                  <p className="text-gray-400 text-xs mt-1 line-clamp-2">{cert.description || "No description provided."}</p>
                  <div className="flex gap-1.5 mt-2 flex-wrap">
                    {cert.featured && <span className="badge-gold text-[10px]">★ Featured on Home</span>}
                    {!cert.isActive && <span className="bg-red-50 text-red-500 text-[10px] px-2 py-0.5 rounded-md font-semibold">Inactive</span>}
                  </div>
                </div>
              </div>
              <div className="px-5 py-3.5 bg-gray-50/50 border-t border-gray-100 flex gap-2">
                <button onClick={() => { setEditItem(cert); setForm({ ...emptyCert, ...cert }); setShowForm(true); }} className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-brand-pale text-brand-primary text-xs font-semibold hover:bg-brand-muted transition-colors"><Edit2 className="w-3.5 h-3.5" /> Edit</button>
                <button onClick={() => handleDelete(cert.id)} className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-red-50 text-red-500 text-xs font-semibold hover:bg-red-100 transition-colors"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
              </div>
            </div>
          ))}
          {items.length === 0 && (
            <div className="col-span-3 text-center py-20 bg-white rounded-2xl border border-dashed border-gray-200">
              <span className="text-5xl block mb-3">🏆</span>
              <p className="text-gray-400 font-medium">No certifications yet. Add your first certificate.</p>
            </div>
          )}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading font-bold text-gray-900 text-lg">
                {editItem ? "Edit Certification" : "Add Certification"}
              </h3>
              <button onClick={() => setShowForm(false)} className="w-8 h-8 rounded-xl bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Certification Name *</label>
                <input className="input-field" placeholder="e.g. ISO 22000:2018 Certificate" value={form.name} onChange={setE("name")} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Description</label>
                <textarea className="input-field resize-none text-xs" rows={3} placeholder="Brief details about certification..." value={form.description} onChange={setE("description")} />
              </div>
              
              {/* Logo Url */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Certificate Logo (shown on home/about list)</label>
                <div className="flex gap-2">
                  <input className="input-field text-xs flex-1" placeholder="Logo image URL..." value={form.logoUrl} onChange={setE("logoUrl")} />
                  <CertImageUpload onUploaded={url => f("logoUrl")(url)} label="Upload Logo" />
                </div>
                {form.logoUrl && (
                  <img src={getImageUrl(form.logoUrl)} alt="Logo Preview" className="mt-2 h-14 w-auto object-contain border border-gray-100 rounded-lg p-1" />
                )}
              </div>

              {/* Full Image Url */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Certificate Full Document / Image</label>
                <div className="flex gap-2">
                  <input className="input-field text-xs flex-1" placeholder="Full image URL..." value={form.imageUrl} onChange={setE("imageUrl")} />
                  <CertImageUpload onUploaded={url => f("imageUrl")(url)} label="Upload Document" />
                </div>
                {form.imageUrl && (
                  <img src={getImageUrl(form.imageUrl)} alt="Doc Preview" className="mt-2 h-20 w-auto object-cover border border-gray-100 rounded-lg" />
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Sort Order</label>
                  <input type="number" className="input-field text-sm" value={form.sortOrder} onChange={setE("sortOrder")} />
                </div>
                <div className="flex flex-col gap-2 pt-6">
                  <label className="flex items-center gap-1.5 cursor-pointer text-xs font-semibold text-gray-700">
                    <input type="checkbox" checked={form.featured} onChange={setC("featured")} className="accent-brand-primary w-3.5 h-3.5" />
                    Featured on Home
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer text-xs font-semibold text-gray-700">
                    <input type="checkbox" checked={form.isActive} onChange={setC("isActive")} className="accent-brand-primary w-3.5 h-3.5" />
                    Active / Visible
                  </label>
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowForm(false)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="flex-1 btn-primary justify-center py-2.5 text-sm">
                {saving ? <Loader className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" />Save</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
